import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { BadgeChip } from "@/components/BadgeChip";
import { Pill, RadialGauge } from "@/components/perf";
import { toast } from "sonner";
import { X, MapPin, Crown, Loader2, Activity, Repeat, UserPlus, UserCheck, Trophy, Swords, Flame, Video, Medal, MessageSquare, Heart, CalendarDays, Layers, Flag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PlayerVaultModal from "@/components/PlayerVaultModal";
import TournamentHistoryModal from "@/components/TournamentHistoryModal";

const StatTile = ({ icon: Icon, label, value, color = "#A3E635", sub, testId }) => (
  <div className="bg-pb-elevated border border-pb-border p-3" data-testid={testId}>
    <div className="flex items-center justify-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="font-display font-black text-xl" style={{ color }}>{value}</div>
    {sub && <div className="text-[9px] text-zinc-600 mt-0.5">{sub}</div>}
  </div>
);

export default function PlayerCardModal({ playerId, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [badges, setBadges] = useState([]);
  useEffect(() => {
    api.get(`/pbr/achievements/${playerId}`).then(({ data }) => setBadges(data)).catch(() => {});
  }, [playerId]);
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [follow, setFollow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!playerId) return;
    setP(null); setError(false); setFollow(null); setShowVault(false);
    api.get(`/players/${playerId}/public`).then(({ data }) => setP(data)).catch(() => setError(true));
    api.get(`/members/${playerId}/follow-info`).then(({ data }) => setFollow(data)).catch(() => {});
  }, [playerId]);

  const toggleFollow = async () => {
    setBusy(true);
    try {
      const { data } = follow?.is_following
        ? await api.delete(`/members/${playerId}/follow`)
        : await api.post(`/members/${playerId}/follow`);
      setFollow(data);
      if (data.is_following) toast.success(`Following ${p?.name || "member"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!playerId) return null;
  const isSelf = user?.id === playerId;
  const b = p?.bragging || {};
  const joined = p?.joined ? new Date(p.joined).toLocaleDateString([], { month: "short", year: "numeric" }) : null;

  return (
    <div className="fixed inset-0 z-[75] bg-black/75 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="player-card-modal" className="w-full max-w-sm max-h-[90vh] overflow-y-auto bg-pb-surface border border-pb-border p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end mb-2">
          <button data-testid="player-card-close" onClick={onClose}><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
        </div>
        {error ? (
          <div className="text-sm text-zinc-500 text-center pb-6">Player profile unavailable.</div>
        ) : !p ? (
          <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-pb-cyan animate-spin" /></div>
        ) : (
          <div className="flex flex-col items-center text-center pb-2">
            {p.featured_card ? (
              <img src={p.featured_card} alt={`${p.name} sports card`} className="w-48 border border-amber-400/40" data-testid="player-card-featured" />
            ) : (
              <Avatar src={p.avatar} name={p.name} size={96} testId="player-card-avatar" />
            )}
            <div className="font-display font-black text-2xl uppercase mt-4 flex items-center gap-2">
              {p.membership === "vip" && <Crown className="w-5 h-5 text-pb-red" />}{p.name}
              {b.titles > 0 && (
                <span data-testid="player-card-champion-badge" title={`Tournament Champion — ${b.titles} title${b.titles === 1 ? "" : "s"}`}>
                  <Trophy className="w-6 h-6 text-yellow-400 fill-yellow-400 drop-shadow-[0_0_6px_rgba(250,204,21,0.7)]" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1">
              {p.location && (
                <span className="flex items-center gap-1.5" data-testid="player-card-location">
                  <MapPin className="w-3.5 h-3.5 text-pb-cyan" /> {p.location}
                </span>
              )}
              {joined && (
                <span className="flex items-center gap-1.5" data-testid="player-card-joined">
                  <CalendarDays className="w-3.5 h-3.5 text-zinc-500" /> Since {joined}
                </span>
              )}
            </div>
            {follow && (
              <div className="flex items-center gap-4 mt-3 text-xs text-zinc-400" data-testid="player-card-follow-counts">
                <span><b className="text-white font-display">{follow.followers}</b> followers</span>
                <span><b className="text-white font-display">{follow.following}</b> following</span>
                {follow.follows_me && <span className="text-[9px] uppercase tracking-widest text-pb-cyan border border-pb-cyan/40 px-1.5 py-0.5">Follows you</span>}
              </div>
            )}
            <div className="flex gap-2 mt-3 flex-wrap justify-center">
              {!isSelf && follow && (
                <button data-testid="player-card-follow-btn" onClick={toggleFollow} disabled={busy}
                  className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest px-6 py-2 transition-all active:scale-95 disabled:opacity-50 ${
                    follow.is_following ? "border border-pb-border text-zinc-300 hover:border-red-500 hover:text-red-400" : "bg-pb-red text-black hover:bg-[#BEF264]"}`}>
                  {follow.is_following ? <><UserCheck className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
                </button>
              )}
              <button data-testid="player-card-view-vault-btn" onClick={() => setShowVault(true)}
                className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest px-6 py-2 border border-pb-cyan/50 text-pb-cyan hover:bg-pb-cyan hover:text-black transition-all active:scale-95">
                <Layers className="w-3.5 h-3.5" /> {isSelf ? "My Vault" : "View Their Vault"}
              </button>
              <button data-testid="player-card-history-btn" onClick={() => setShowHistory(true)}
                className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest px-6 py-2 border border-yellow-500/50 text-yellow-400 hover:bg-yellow-400 hover:text-black transition-all active:scale-95">
                <Trophy className="w-3.5 h-3.5" /> Tournament History
              </button>
              <button data-testid="player-card-rtn-btn" onClick={() => { navigate(`/road-to-nationals/${playerId}`); onClose(); }}
                className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest px-6 py-2 border border-[#BEF264]/60 text-[#BEF264] hover:bg-[#BEF264] hover:text-black transition-all active:scale-95">
                <Flag className="w-3.5 h-3.5" /> Road to Nationals
              </button>
              {badges.length > 0 && (
                <div className="w-full flex flex-wrap justify-center gap-1.5 mt-1" data-testid="player-card-badges">
                  {badges.map((b) => (
                    <span key={b.badge} className="border border-yellow-500/40 bg-yellow-500/5 px-2 py-0.5 text-[8px] font-black uppercase tracking-wider text-yellow-400" title={b.source || ""}>🏅 {b.badge}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap justify-center">
              <Pill color="#A3E635">{p.division}</Pill>
              {b.titles > 0 && <Pill color="#FACC15">🏆 {b.titles}× Champion</Pill>}
              {p.badge && <BadgeChip badge={p.badge} />}
              <Pill color="#4ADE80">{p.membership} member</Pill>
            </div>
            <div className="my-4">
              <RadialGauge value={p.skill_index ?? 0} label="Skill Index" color="#A3E635" size={140} />
            </div>
            <div className="text-xs text-zinc-500 mb-4">Rating {typeof p.rating === "number" ? p.rating.toFixed(2) : p.rating}</div>

            {/* Match record banner */}
            {b.matches > 0 && (
              <div className="w-full bg-pb-elevated border border-pb-border p-3 mb-3 flex items-center justify-around" data-testid="player-card-record">
                <div>
                  <div className="font-display font-black text-xl"><span className="text-pb-red">{b.wins}</span><span className="text-zinc-600 mx-1">-</span><span className="text-zinc-400">{b.losses}</span></div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Record</div>
                </div>
                <div>
                  <div className="font-display font-black text-xl text-pb-cyan">{b.win_rate}%</div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Win Rate</div>
                </div>
                <div>
                  <div className="font-display font-black text-xl text-orange-400 flex items-center gap-1 justify-center"><Flame className="w-4 h-4" />{b.best_streak}</div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Best Streak</div>
                </div>
              </div>
            )}

            {/* Rivalry record (head-to-head vs viewer) */}
            {p.rivalry && (
              <div className="w-full border border-[#F472B6]/50 bg-[#F472B6]/10 p-3 mb-3" data-testid="player-card-rivalry">
                <div className="flex items-center justify-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
                  <Swords className="w-3 h-3 text-[#F472B6]" /> Rivalry Record — You vs {p.name.split(" ")[0]}
                </div>
                <div className="font-display font-black text-2xl">
                  <span className="text-[#F472B6]">{p.rivalry.wins}</span>
                  <span className="text-zinc-600 mx-1.5">–</span>
                  <span className="text-zinc-400">{p.rivalry.losses}</span>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
                  {p.rivalry.wins > p.rivalry.losses ? "You lead this rivalry 🔥" : p.rivalry.wins < p.rivalry.losses ? `${p.name.split(" ")[0]} leads — time for revenge` : "Dead even — next game decides"}
                </div>
              </div>
            )}

            {/* Top rivalries list */}
            {p.rivalries?.length > 0 && (
              <div className="w-full bg-pb-elevated border border-pb-border p-3 mb-3" data-testid="player-card-rivalries">
                <div className="flex items-center justify-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  <Swords className="w-3 h-3 text-[#F472B6]" /> Top Rivalries
                </div>
                <div className="space-y-1.5">
                  {p.rivalries.map((r) => (
                    <div key={r.user_id} className="flex items-center justify-between text-sm" data-testid={`rivalry-row-${r.user_id}`}>
                      <span className="text-zinc-300 truncate">vs {r.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="font-display font-black">
                          <span className="text-pb-red">{r.wins}</span><span className="text-zinc-600">–</span><span className="text-zinc-400">{r.losses}</span>
                        </span>
                        {(r.challenge_wins > 0 || r.challenge_losses > 0) && (
                          <span className="text-[10px] font-bold text-[#F472B6]" data-testid={`rivalry-challenge-${r.user_id}`} title="Record from accepted challenges">⚔ {r.challenge_wins}–{r.challenge_losses}</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="text-[8px] uppercase tracking-widest text-zinc-600 mt-2 text-center">
                  white = all rec games · <span className="text-[#F472B6]">⚔ pink = challenge games</span>
                </div>
              </div>
            )}

            {/* Titles banner */}
            {(b.titles > 0 || b.runner_ups > 0) && (
              <div className="w-full border border-yellow-500/40 bg-yellow-500/5 p-2.5 mb-3 flex items-center justify-center gap-4" data-testid="player-card-titles">
                {b.titles > 0 && <span className="flex items-center gap-1.5 text-yellow-400 font-display font-black text-sm uppercase"><Trophy className="w-4 h-4" /> {b.titles} Title{b.titles === 1 ? "" : "s"}</span>}
                {b.runner_ups > 0 && <span className="flex items-center gap-1.5 text-zinc-300 font-display font-black text-sm uppercase"><Medal className="w-4 h-4" /> {b.runner_ups} Runner-Up</span>}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 w-full" data-testid="player-card-stats-grid">
              <StatTile icon={Repeat} label="Total Reps" value={p.total_reps} color="#F87171" testId="player-card-reps" />
              <StatTile icon={Activity} label="Sessions" value={p.sessions} color="#22D3EE" testId="player-card-sessions" />
              <StatTile icon={Swords} label="Matches" value={b.matches ?? 0} color="#A3E635"
                sub={b.matches ? `${b.tournament_matches || 0} tourney · ${b.league_matches || 0} league` : null} testId="player-card-matches" />
              <StatTile icon={Trophy} label="Tournaments" value={b.tournaments_played ?? 0} color="#FACC15" testId="player-card-tournaments" />
              <StatTile icon={Video} label="Films Graded" value={b.videos_graded ?? 0} color="#C084FC"
                sub={b.video_best ? `Best score ${b.video_best}` : null} testId="player-card-videos" />
              <StatTile icon={Heart} label="Likes Earned" value={b.likes_received ?? 0} color="#FB7185"
                sub={b.posts ? `${b.posts} post${b.posts === 1 ? "" : "s"}` : null} testId="player-card-likes" />
            </div>

            {(b.dupr || b.vair) && (
              <div className="flex gap-2 mt-3" data-testid="player-card-ratings">
                {b.dupr && <Pill color="#22D3EE">DUPR {Number(b.dupr).toFixed(2)}</Pill>}
                {b.vair && <Pill color="#C084FC">VAIR {Number(b.vair).toFixed(2)}</Pill>}
              </div>
            )}
          </div>
        )}
      </div>
      {showVault && p && <PlayerVaultModal playerId={playerId} playerName={p.name} onClose={() => setShowVault(false)} />}
      {showHistory && p && <TournamentHistoryModal playerId={playerId} playerName={p.name} onClose={() => setShowHistory(false)} />}
    </div>
  );
}
